import React from 'react'

import { theme } from '@acorns/grove'
import { AcornsIcon } from '@acorns/icons'
import styled from 'styled-components'

import { DeepLinks } from 'components/AIAssistantComponent/DeepLinks'
import { Rating } from 'components/AIAssistantComponent/Rating'
import { SupportCitation } from 'generated/types'
import { AI_CHAT_RESPONSE_MESSAGE } from 'utils/testing/testIDs'

import { ChatWidgetContainer } from './ChatWidgetContainer'
import { ChoiceWidget } from './ChoiceWidget'
import { Citations } from './Citations'
import {
  RatingMutationPayload,
  ResponseMessage as ResponseMessageType,
} from './utils/types'

const MessageContainerBox = styled.div`
  width: 100%;
  margin-bottom: 10px;
  padding: 0px 20px;
`

const MessageContainer = styled.div`
  display: inline-block;
`

const InternalMessageBox = styled.div<{ showRating?: boolean }>`
  max-width: calc(100% - 40px);
  background-color: ${theme.colors.primaryWhite};
  padding: 10px;
  width: fit-content;
  color: ${theme.colors.primarySlate};
  border-radius: ${theme.borderRadius.large};
  font-size: 14px;
  display: inline-flex;
  @media screen and (max-width: ${theme.dimensions.breakpointsSmall}px) {
    max-width: calc(100% - 50px);
  }
`

const AcornContainer = styled.div`
  width: 40px;
  height: 40px;
  padding: 10px;
  border-radius: 50%;
  background-color: ${theme.colors.primaryGreen};
  display: inline-flex;
  vertical-align: top;
  margin-top: 10px;
`

const Acorn = styled(AcornsIcon)`
  width: 20px;
  height: 20px;
  path {
    fill: ${theme.colors.primaryWhite};
  }
`

export const ResponseMessage = ({
  message,
  onRateResponse,
  onCloseAssistant,
  sessionId,
  onSendMessage,
  isTyping,
}: {
  message?: ResponseMessageType
  onRateResponse: ({ messageId, rating }: RatingMutationPayload) => void
  onCloseAssistant: () => void
  sessionId: string
  onSendMessage: (message: string) => void
  isTyping: boolean
}) => {
  const showRating = message?.canRate

  if (message) {
    return (
      <MessageContainerBox
        data-testid={`${AI_CHAT_RESPONSE_MESSAGE}_${message?.id}`}
      >
        <MessageContainer>
          <AcornContainer>
            <Acorn icon={AcornsIcon.Icon.AcornsLogo} />
          </AcornContainer>
          <InternalMessageBox showRating={showRating}>
            {message.text?.split('\n').map((line, index) => (
              <React.Fragment key={index}>
                {line}
                <br />
              </React.Fragment>
            ))}
          </InternalMessageBox>
        </MessageContainer>

        {(message.deeplinks || []).length > 0 && (
          <DeepLinks
            deeplinks={message.deeplinks ?? []}
            onCloseAssistant={onCloseAssistant}
          />
        )}
        {(message.customerSupportChatWidgets || []).length > 0 && (
          <ChatWidgetContainer
            chatWidgets={message.customerSupportChatWidgets ?? []}
            onCloseAssistant={onCloseAssistant}
            sessionId={sessionId}
          />
        )}
        {(message.citations || []).length > 0 && (
          <Citations
            citations={(message.citations ?? []) as SupportCitation[]}
          />
        )}
        {message.chatChoices && (
          <ChoiceWidget
            chatChoices={message.chatChoices}
            onSendMessage={onSendMessage}
            isDisabled={isTyping}
          />
        )}
        {showRating && (
          <Rating
            rating={message.rating}
            messageId={message.id}
            onRateResponse={onRateResponse}
            input={message.text || ''}
          />
        )}
      </MessageContainerBox>
    )
  } else {
    return <></>
  }
}
